import { SolanaGatewayToken } from "@civic/solana-gateway-react/dist/esm/types";
import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import { BN, Program, web3 } from "@project-serum/anchor";
import {
  deserializeBorsh,
  getRealm,
  Governance,
  GOVERNANCE_SCHEMA,
  ProgramAccount,
} from "@solana/spl-governance";
import {
  MintMaxVoteWeightSource as MintMaxVoteWeightSourceV3,
  withCreateRealm as withCreateRealmV3,
} from "spl-governance-v2";
import { NATIVE_MINT, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import { chunk } from "loadsh";
import { add, divide, pow, round } from "mathjs";

import { getClubs } from "../../api/clubs.api";
import {
  MAX_SELL_PERMISSIONS_FOR_CLUB,
  MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX,
} from "../../common/constants/club.constants";
import {
  CHAIN_ID,
  EMPTY_STRING,
  MESSAGE_TYPE,
  NATIVE_MINT_DECIMAL_PLACES,
  REMAINING_ACCOUNTS_PER_IX,
  ROLES_PER_IX,
  SPL_PROGRAM_VERSION_V3,
  TREASURY_INDEX,
} from "../../common/constants/common.constants";
import { RestrictionOptions } from "../../common/enums/civic.enum";
import {
  ClubCreationAccounts,
  ClubType,
  InvitationStatus,
  SequenceType,
  TreasuryFundraiseStatus,
} from "../../common/enums/club.enum";
import { GovernanceConfigurationType } from "../../common/enums/clubs-wizard.enum";
import {
  ClubActionSolana,
  Role,
  TradedRightType,
  UserRole,
} from "../../common/enums/common.enums";
import {
  ICreateClubWizardForm,
  IVotingThreshold,
} from "../../common/interfaces/clubs-wizard.interface";
import {
  IBasicClubData,
  IClubBasicInfo,
  IClubDataSolana,
  IClubMemberBasicInfo,
  IClubTreasury,
  IDepositRecord,
  IFundraise,
  IMembersRights,
  ISellPermissionConfigSolana,
  ISolanaAllocation,
  ISolanaClubMember,
  ISolanaCustomAllocation,
  ISolanaReservedRights,
  ITreasuryTokenAccInfo,
  IVault,
  RoleConfig,
} from "../../common/interfaces/clubs.interface";
import {
  IDetailNftData,
  IInstructionSet,
} from "../../common/interfaces/common.interface";
import {
  getDecimalsPerMint,
  getNftsFromRPC,
  getTreasuryCount,
} from "../../utilities/helpers";
import { createNotification } from "../../utilities/notification";
import { ClubProgram } from "../club_program_develop";
import {
  createTokenAccountOwnedBySpecifiedAccount,
  extractNumbersFromString,
  getDenominatedTokenInfo,
  getDepositAmountForMemberBasedOnSelectedFundraises,
  getProfitForTreasury,
  getRoleConfiguration,
  getTotalFundraisedAmount,
  getTreasuyTokenAccountsInfo,
  mapClubActionFromSolanaToClubAction,
  mapSellPermission,
  parseReserverRightsFromProgramToTreasuryInfo,
} from "../common/helpers/common.helpers";
import { getNftTokenAccountsByOwner } from "../common/helpers/nftClubs.helpers";
import {
  accountGovernanceSeed,
  changeClubConfigGovernance,
  denominatedTokenSeed,
  financialRecordSeed,
  fundraiseCfgSeed,
  governanceSeed,
  mainSplGovProgram,
  mint,
  monkeAdminSeed,
  monkeMint,
  profitSeed,
  programFactory,
  realmConfigSeed,
  RPC_CONNECTION,
  sellPermissionGovernanceSeed,
  splGovernanceProgram,
  transferSeed,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  vaultDataSeed,
  vaultSeed,
  voterWeightSeed,
} from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";
import { sendTransactions } from "../transactions/sendTransactions";
import { IExistingInvestment } from "../../components/UploadInvestments/upload-investment.interface";

const mapSellPermissionToArray = (
  values: IVotingThreshold[],
  decimals: number
): ISellPermissionConfigSolana[] => {
  return values.map((item) => {
    return {
      from: new BN(item.from * Number(pow(10, decimals))),
      to: new BN(item.to * Number(pow(10, decimals))),
      decimal: 9,
      quorumMinimum: item.approvalQuorum,
    };
  });
};

const getClubType = (clubType: string): BN => {
  switch (clubType) {
    case GovernanceConfigurationType.RoleBased:
      return new BN(2);
    case GovernanceConfigurationType.NFT:
      return new BN(3);
    case GovernanceConfigurationType.ExistingToken:
      return new BN(0);
    default:
      return new BN(0);
  }
};

/**
 * Get accounts of initial club members
 * @param values {ICreateClubWizardForm} Club Address
 * @param realmAddress {PublicKey} Club Address
 * @return {Promise<{ remainingAccounts: AccountMeta[], roles: Role[]}>}
 */
const getInitialMembersAccounts = async (
  values: ICreateClubWizardForm,
  realmAddress: PublicKey
) => {
  const remainingAccounts: AccountMeta[] = [];
  const roles: string[] = [];
  const clubProgram = programFactory();

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  for (const member of values.initialMembers) {
    if (member.role === UserRole.owner) {
      continue;
    }
    roles.push(member.role);
    const [allowedMemberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        new PublicKey(member.pubKey).toBuffer(),
      ],
      clubProgram.programId
    );
    remainingAccounts.push(
      {
        pubkey: new PublicKey(member.pubKey),
        isWritable: false,
        isSigner: false,
      },
      {
        pubkey: allowedMemberDataAddress,
        isWritable: true,
        isSigner: false,
      }
    );
  }
  let remainingAccountsArray: AccountMeta[][] = [];
  let rolesArray: string[][] = [];
  if (remainingAccounts.length >= REMAINING_ACCOUNTS_PER_IX) {
    remainingAccountsArray = chunk(
      remainingAccounts,
      REMAINING_ACCOUNTS_PER_IX
    );
    rolesArray = chunk(roles, ROLES_PER_IX);
    return { remainingAccountsArray, roles, rolesArray };
  } else {
    remainingAccountsArray.push(remainingAccounts);
    return {
      remainingAccountsArray,
      roles,
      rolesArray,
    };
  }
};

/**
 * Fetch basic club info from Solana
 * @param clubAddress {string} Club Address
 * @return {IClubDataSolana | null}
 */
export async function fetchBasicClubInfoFromSolana(
  clubAddress: string
): Promise<IClubDataSolana | null> {
  const clubProgram = programFactory();
  const club = await clubProgram.account.clubData.fetch(clubAddress);
  const realm = await getRealm(RPC_CONNECTION, club.realm);
  const governances = await getGovernaceAccountsForRealm(realm.account.name);

  const roleConfigRaw: any = club.roleConfig;
  const roleConfig: RoleConfig[] = roleConfigRaw.map((item) => {
    return {
      name: item.name,
      roleWeight: item.roleWeight.toNumber(),
      actions: item.clubActions.map((item) =>
        mapClubActionFromSolanaToClubAction(
          Object.keys(item).toString() as ClubActionSolana
        )
      ),
    };
  });

  return {
    title: club.name as string,
    ownerAddress: club.authority,
    clubAddress,
    clubType: roleConfig.find((item) => item.name === UserRole.investor)
      ? ClubType.ProjectFundraise
      : club.clubType,
    clubMint: realm.account.communityMint,
    roleConfig: roleConfig,
    stakeCount: club.stakeCount,
    governanceAccounts: governances,
    numberOfMembers: club.numberOfMembers,
    kycConfig: club.kycConfig
      ? {
          location: club.kycConfig?.location,
          required: club.kycConfig.required,
        }
      : undefined,
  };
}

/**
 * Fetch basic info for all clubs from Solana
 * @return {Promise<IClubDataSolana[]>} All clubs from Solana
 */
export async function fetchAllClubsFromSolana(): Promise<IClubDataSolana[]> {
  const clubProgram = programFactory();
  const clubs = await clubProgram.account.clubData.all();

  return clubs.map((club) => {
    return {
      clubAddress: club.publicKey,
      title: club.account.name as string,
      ownerAddress: club.account.authority,
      clubType: club.account.clubType,
      numberOfMembers: club.account.numberOfMembers,
    };
  });
}

//TODO@milica-monke-merge: Monke nft parameter is only for monke
/**
 * Fetch all clubs from Solana with sort and filter required data
 * @return {Promise<IBasicClubData[]>}
 */
export async function fetchAllClubsFromSolanaWithDetails(): Promise<
  IBasicClubData[]
> {
  const clubProgram = programFactory();
  const clubs = await clubProgram.account.clubData.all();
  const allMembersData = await getMembersAllowedDataForClubs();
  const clubsFromApi: IBasicClubData[] = await getClubs();
  const treasuryDataPdas: PublicKey[] = [];
  const fundraisePdas: PublicKey[] = [];
  const vaultPdas: PublicKey[] = [];
  let detailedClubs: IBasicClubData[] = [];

  for (const solanaClub of clubs) {
    const club: IBasicClubData = clubsFromApi[solanaClub.publicKey.toString()];

    if (club) {
      const [clubData] = await PublicKey.findProgramAddress(
        [unqClubSeed, solanaClub.account.realm.toBuffer()],
        clubProgram.programId
      );
      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          clubData.toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
        ],
        clubProgram.programId
      );
      const [treasuryData] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
        clubProgram.programId
      );
      treasuryDataPdas.push(treasuryData);
      const [vaultAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          clubData.toBuffer(),
          treasury.toBuffer(),
          vaultSeed,
          Buffer.from(CHAIN_ID),
        ],
        clubProgram.programId
      );
      vaultPdas.push(vaultAddress);
    }
  }

  const treasuryDataAccounts: any[] =
    await clubProgram.account.treasuryData.fetchMultiple(treasuryDataPdas);

  for (const [index, treasuryData] of treasuryDataAccounts.entries()) {
    if (treasuryData === null) {
      //Treasury is not created during the club creation proccess, so we do not have fundraises
      continue;
    }
    for (let i = 1; i <= treasuryData.fundraiseCount; i++) {
      const countBuff = Buffer.alloc(4);
      countBuff.writeInt32LE(i, 0);

      const [fundraiseAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          treasuryDataPdas[index].toBuffer(),
          fundraiseCfgSeed,
          countBuff,
        ],
        clubProgram.programId
      );
      fundraisePdas.push(fundraiseAddress);
    }
  }
  const fundraiseAccounts: any =
    await clubProgram.account.fundraiseConfig.fetchMultiple(fundraisePdas);

  for (const solanaClub of clubs) {
    if (
      (solanaClub.account.roleConfig as any[]).find(
        (role) => role.name === UserRole.investor
      )
    )
      continue;
    const club: IBasicClubData = clubsFromApi[solanaClub.publicKey.toString()];
    if (club) {
      const [clubData] = await PublicKey.findProgramAddress(
        [unqClubSeed, solanaClub.account.realm.toBuffer()],
        clubProgram.programId
      );
      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          clubData.toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
        ],
        clubProgram.programId
      );
      const clubsFundraiseAccounts = fundraiseAccounts.filter(
        (item) => item.treasury.toString() === treasury.toString()
      );

      const treasuryDataAccount = treasuryDataAccounts
        .filter((item) => item !== null)
        .find((item) => item.treasury.toString() === treasury.toString());
      club.activeFundraise = treasuryDataAccount
        ? treasuryDataAccount.hasActiveFundraise
        : false;
      club.totalFundraisedAmount = getTotalFundraisedAmount(
        clubsFundraiseAccounts
      );

      const allMembersDataForClub = allMembersData.filter(
        (item) => item.account.clubData.toString() === club.address
      );

      club.numberOfMembers = allMembersDataForClub.length;
      club.createdAt = new Date(
        clubsFromApi[solanaClub.publicKey.toString()].createdAt
      );

      club.communityMintAddress =
        clubsFromApi[solanaClub.publicKey.toString()].communityMintAddress;
      const roleConfigRaw: any = solanaClub.account.roleConfig;
      club.roleConfig = roleConfigRaw.map((item) => {
        return {
          name: item.name,
          roleWeight: item.roleWeight.toNumber(),
          actions: item.clubActions.map((item) =>
            mapClubActionFromSolanaToClubAction(
              Object.keys(item).toString() as ClubActionSolana
            )
          ),
        };
      });
      club.clubType =
        solanaClub.account.clubType === ClubType.TokenProportional &&
        club.roleConfig.find((item) => item.name === UserRole.investor)
          ? ClubType.ProjectFundraise
          : solanaClub.account.clubType;
      detailedClubs.push(club);
    }
  }

  detailedClubs.sort((firstClub, secondClub) =>
    firstClub.createdAt.getTime() < secondClub.createdAt.getTime() ? 1 : -1
  );

  return detailedClubs;
}

//TODO@Milica: check return type
/**
 * Get members for all clubs
 * @return {Promise<any>}
 */
export const getMembersAllowedDataForClubs = async () => {
  const clubProgram = programFactory();

  try {
    const members = await clubProgram.account.allowedMemberData.all();
    return members.filter(
      (item) => item.account.status === InvitationStatus.Accepted
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Get all members for a club
 * @param clubBasicInfo {IClubBasicInfo} Club basic info object
 * @param treasury {IClubTreasury} Club treasury object
 * @param roleConfig? {RoleConfig} User's role
 * @return {ISolanaClubMember[]} Array of members
 */
export async function getClubMembers(
  clubBasicInfo: IClubBasicInfo
): Promise<ISolanaClubMember[]> {
  const clubProgram = programFactory();
  const solanaMembers: ISolanaClubMember[] = [];
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubBasicInfo.name)],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  try {
    const members = await clubProgram.account.allowedMemberData.all();
    for (const member of members) {
      if (
        member.account.clubData.toString() === clubData.toString() &&
        member.account.status === InvitationStatus.Accepted
      ) {
        solanaMembers.push({
          pubkey: member.account.memberPubkey,
          role: member.account.role,
        });
      }
    }
  } catch (error: any) {
    console.log(error);
    createNotification(
      MESSAGE_TYPE.ERROR,
      "Failed to get all members from Solana",
      error.message
    );
  }

  return solanaMembers;
}

export async function getClubMembersAndInvitations(
  clubBasicInfo: IClubBasicInfo
) {
  const clubProgram = programFactory();
  const solanaMembers: ISolanaClubMember[] = [];
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubBasicInfo.name)],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  try {
    const members = await clubProgram.account.allowedMemberData.all();
    for (const member of members) {
      if (member.account.clubData.toString() === clubData.toString()) {
        solanaMembers.push({
          pubkey: member.account.memberPubkey,
          role: member.account.role,
          isAccepted: member.account.status === InvitationStatus.Accepted,
        });
      }
    }
  } catch (error: any) {
    console.log(error);
    createNotification(
      MESSAGE_TYPE.ERROR,
      "Failed to get all members from Solana",
      error.message
    );
  }

  return solanaMembers;
}

/**
 * Create UNQ club on Solana
 * @param values {ICreateClubWizardForm} Values from Wizard form
 * @param curator {AnchorWallet} Curator for the club
 * @return {{ realmAddress, clubAddress }}
 */
export async function createClubOnSolana(
  values: any,
  curator: AnchorWallet,
  gatewayToken: SolanaGatewayToken | undefined,
  retry?: boolean,
  accountsNotCreated?: string[],
  goToClubDetails?: (clubAddress: string) => void
): Promise<{ realmAddress; clubAddress }> {
  const clubProgram = programFactory(curator);
  const realmAuthority: PublicKey = curator.publicKey;
  let treasuryRemainingAcc: AccountMeta[] = [];
  const instructions: TransactionInstruction[] = [];
  const createOgRealmIx: TransactionInstruction[] = [];

  const countBuff = Buffer.alloc(4);
  countBuff.writeInt32LE(1, 0);

  //Note: syndicate specific - For monke monkeMint, for main unq mint (mint)
  const collectionMint =
    values.governance === GovernanceConfigurationType.NFT
      ? new PublicKey(
          values.nftMintAddress !== EMPTY_STRING
            ? values.nftMintAddress
            : values.collectionMintAddress
        )
      : mint;

  const treasuryBuffer = Buffer.alloc(4);
  treasuryBuffer.writeInt32LE(TREASURY_INDEX + 1, 0);

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(values.clubName)],
    splGovernanceProgram
  );

  const { remainingAccountsArray, roles, rolesArray } =
    await getInitialMembersAccounts(values, realmAddress);

  const [communityTokenHoldingAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, realmAddress.toBuffer(), collectionMint.toBuffer()],
    splGovernanceProgram
  );
  const [realmConfigAddress] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasury] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubData.toBuffer(), treasurySeed, treasuryBuffer],
    clubProgram.programId
  );
  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );
  const [governanceAddress] = await PublicKey.findProgramAddress(
    [accountGovernanceSeed, realmAddress.toBuffer(), treasury.toBuffer()],
    splGovernanceProgram
  );
  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      collectionMint.toBuffer(),
      curator.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      curator.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [vaultAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasury.toBuffer(),
      vaultSeed,
      Buffer.from(CHAIN_ID),
    ],
    clubProgram.programId
  );
  const [vaultDataAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasury.toBuffer(),
      vaultDataSeed,
      Buffer.from(CHAIN_ID),
    ],
    clubProgram.programId
  );

  const [fundraiseAddress] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasuryData.toBuffer(), fundraiseCfgSeed, countBuff],
    clubProgram.programId
  );

  const [voterWeightAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      voterWeightSeed,
      curator.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [profit] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), profitSeed],
    clubProgram.programId
  );

  const { tokenAccount, createAccountIx, createInitAccountInstruction } =
    await createTokenAccountOwnedBySpecifiedAccount(
      curator.publicKey,
      profit,
      NATIVE_MINT
    );

  const createProfitTokenAccountIx: TransactionInstruction[] = [];
  createProfitTokenAccountIx.push(createAccountIx);
  createProfitTokenAccountIx.push(createInitAccountInstruction);

  const sellPermissions = mapSellPermissionToArray(
    values.votingThreshold,
    values.mintDecimals
  );
  const sellPermissionsForSeparateTx: ISellPermissionConfigSolana[] =
    sellPermissions.length > MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX
      ? sellPermissions.slice(MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX)
      : [];
  const sellPermissionsRemainingAccounts =
    await getSellPermissionsRemainingAccounts(
      sellPermissions.length > MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX
        ? sellPermissions.slice(
            0,
            MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX
          )
        : sellPermissions,
      realmAddress,
      clubProgram
    );

  const clubType = getClubType(values.governance);

  const roleConfig = getRoleConfiguration(values).map((item) =>
    Buffer.from(item.serializeObj())
  );

  const [monkeAdmins] = await PublicKey.findProgramAddress(
    [unqClubSeed, monkeAdminSeed],
    clubProgram.programId
  );

  try {
    const [ogRealmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(values.clubName)],
      mainSplGovProgram
    );
    //TODO@milica-monke-merge: we dont need to create realm for monke
    // try {
    //   const existingRealm = await getRealm(RPC_CONNECTION, ogRealmAddress);
    // } catch (error) {
    //   await withCreateRealm(
    //     createOgRealmIx,
    //     mainSplGovProgram,
    //     SPL_PROGRAM_VERSION,
    //     values.clubName,
    //     curator.publicKey,
    //     collectionMint,
    //     curator.publicKey,
    //     undefined,
    //     new MintMaxVoteWeightSource({
    //       value: new BN(10),
    //     }),
    //     new BN(10),
    //     clubProgram.programId,
    //     clubProgram.programId
    //   );
    // }

    const kycRemainingAccounts: AccountMeta[] = [];
    if (
      gatewayToken?.publicKey &&
      values.eligibleUsers === RestrictionOptions.VerifiedUsers
    ) {
      kycRemainingAccounts.push({
        pubkey: gatewayToken.publicKey,
        isWritable: false,
        isSigner: false,
      });
    }

    if (!retry) {
      const createClubInstruction = clubProgram.instruction.createClub(
        values.clubName,
        clubType,
        roleConfig,
        UserRole.owner,
        //TODO@milica-roles: check with jovana
        values.governance === GovernanceConfigurationType.NFT
          ? UserRole.member
          : null,
        values.eligibleUsers === RestrictionOptions.VerifiedUsers
          ? {
              required: true,
              location: values.eligibleLocation,
            }
          : null,
        {
          accounts: {
            realm: realmAddress,
            realmAuthority,
            communityTokenHoldingAddress,
            realmConfig: realmConfigAddress,
            tokenOwnerRecord,
            monkeAdmin: monkeAdmins,
            splGovernanceProgram,
            voterWeightProgram: clubProgram.programId,
            communityTokenMint: collectionMint,
            clubData,
            memberData: memberPDA,
            payer: curator.publicKey,
            tokenProgram: TOKEN_PROGRAM_ID,
            rent: web3.SYSVAR_RENT_PUBKEY,
            systemProgram: web3.SystemProgram.programId,
          },
          remainingAccounts: kycRemainingAccounts,
        }
      );
      instructions.push(createClubInstruction);
    }

    //CREATE VAULT
    const vaultInstruction = clubProgram.instruction.createClubVault(CHAIN_ID, {
      accounts: {
        clubData,
        memberData: memberPDA,
        splGovernanceProgram,
        payer: curator.publicKey,
        vaultData: vaultDataAddress,
        vault: vaultAddress,
        treasury,
        systemProgram: web3.SystemProgram.programId,
      },
    });

    instructions.push(vaultInstruction);

    //ADD INITIAL LIST OF MEMBERS
    const addMemberInstructions: TransactionInstruction[] = [];
    if (remainingAccountsArray[0].length > 0) {
      for (const [
        index,
        remainingAccounts,
      ] of remainingAccountsArray.entries()) {
        const membersInstruction = clubProgram.instruction.allowMember(
          rolesArray && rolesArray?.length > 0 ? rolesArray[index] : roles,
          {
            accounts: {
              memberData: memberPDA,
              payer: curator.publicKey,
              clubData,
              systemProgram: web3.SystemProgram.programId,
            },
            remainingAccounts: remainingAccounts,
          }
        );
        addMemberInstructions.push(membersInstruction);
      }
    }
    const createTreasuryGovernanceIx: TransactionInstruction[] = [];

    const updateVW = clubProgram.instruction.updateVoterWeightForGovernance({
      accounts: {
        realm: realmAddress,
        clubData,
        voterWeightRecord: voterWeightAddress,
        memberData: memberPDA,
        payer: curator.publicKey,
        systemProgram: web3.SystemProgram.programId,
      },
    });

    createTreasuryGovernanceIx.push(updateVW);
    if (values.mint.toString() !== NATIVE_MINT.toString()) {
      const [denominatedToken] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), denominatedTokenSeed],
        clubProgram.programId
      );
      treasuryRemainingAcc = [
        {
          pubkey:
            typeof values.mint === "string"
              ? new PublicKey(values.mint)
              : values.mint,
          isSigner: false,
          isWritable: false,
        },
        {
          pubkey: denominatedToken,
          isSigner: false,
          isWritable: true,
        },
      ];
    }

    //CREATE TREASURY
    if (
      !retry ||
      (retry &&
        accountsNotCreated?.includes(ClubCreationAccounts.createTreasury))
    ) {
      const treasuryInstruction =
        clubProgram.instruction.createTreasuryGovernance(
          86400 * values.maxVoteTime + 1,
          values.votingQuorum,
          sellPermissions.slice(
            0,
            MAX_SELL_PERMISSIONS_FOR_TREASURY_GOVERNANCE_IX
          ),
          values.governance === GovernanceConfigurationType.NFT &&
            values.totalNftVotes
            ? new BN(values.totalNftVotes)
            : null,
          {
            accounts: {
              splGovernanceProgram,
              realm: realmAddress,
              realmConfig: realmConfigAddress,
              realmAuthority,
              payer: curator.publicKey,
              tokenOwnerRecord: tokenOwnerRecord,
              governance: governanceAddress,
              voterWeightRecord: voterWeightAddress,
              treasury: treasury,
              profit,
              treasuryData,
              memberData: memberPDA,
              clubData,
              tokenProgram: TOKEN_PROGRAM_ID,
              systemProgram: web3.SystemProgram.programId,
              rent: SYSVAR_RENT_PUBKEY,
            },
            remainingAccounts: [
              ...treasuryRemainingAcc,
              ...sellPermissionsRemainingAccounts,
            ],
          }
        );
      createTreasuryGovernanceIx.push(treasuryInstruction);
    }

    const [governanceAddressVault] = await PublicKey.findProgramAddress(
      [accountGovernanceSeed, realmAddress.toBuffer(), vaultAddress.toBuffer()],
      splGovernanceProgram
    );
    const createWithdrawalGovernance =
      clubProgram.instruction.createWithdrawalGovernance(
        TREASURY_INDEX + 1,
        86400 * values.withdrawalMaxVoteTime + 1,
        values.withdrawalVotingQuorum &&
          values.withdrawalVotingQuorum !== EMPTY_STRING
          ? values.withdrawalVotingQuorum
          : 0,
        // comment this is syndicate only for others we ara using values.votingQuorum,
        {
          accounts: {
            splGovernanceProgram,
            realm: realmAddress,
            realmConfig: realmConfigAddress,
            realmAuthority,
            payer: curator.publicKey,
            tokenOwnerRecord: tokenOwnerRecord,
            governance: governanceAddressVault,
            voterWeightRecord: voterWeightAddress,
            treasury: treasury,
            treasuryData,
            memberData: memberPDA,
            clubData,
            vault: vaultAddress,
            tokenProgram: TOKEN_PROGRAM_ID,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );
    createTreasuryGovernanceIx.push(createWithdrawalGovernance);

    const [governedAccount] = PublicKey.findProgramAddressSync(
      [unqClubSeed, treasury.toBuffer(), changeClubConfigGovernance],
      clubProgram.programId
    );
    const [governanceAddressClubConfig] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmAddress.toBuffer(),
        governedAccount.toBuffer(),
      ],
      splGovernanceProgram
    );
    const createClubConfigGovernanceIx = await clubProgram.methods
      .createChangeClubConfigGovernance(
        values.clubConfigQuorum && values.clubConfigQuorum !== EMPTY_STRING
          ? values.clubConfigQuorum
          : values.votingQuorum,
        86400 * values.clubConfigMaxVoteTime + 1
      )
      .accounts({
        clubData,
        memberData: memberPDA,
        payer: curator.publicKey,
        realm: realmAddress,
        realmAuthority: curator.publicKey,
        voterWeightRecord: voterWeightAddress,
        treasury: treasury,
        treasuryData: treasuryData,
        realmConfig: realmConfigAddress,
        tokenOwnerRecord,
        splGovernanceProgram,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
        goverenedAccount: governedAccount,
        governance: governanceAddressClubConfig,
      })
      .instruction();
    createTreasuryGovernanceIx.push(createClubConfigGovernanceIx);

    const [governedAccountTr] = PublicKey.findProgramAddressSync(
      [unqClubSeed, treasury.toBuffer(), transferSeed],
      clubProgram.programId
    );
    const [governanceAddressTransfer] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmAddress.toBuffer(),
        governedAccountTr.toBuffer(),
      ],
      splGovernanceProgram
    );
    const createTransferGovernance =
      clubProgram.instruction.createTransferGovernance(
        86400 * values.transferMaxVoteTime + 1,
        values.transferVotingQuorum &&
          values.transferVotingQuorum !== EMPTY_STRING
          ? values.transferVotingQuorum
          : 0,
        // comment this is syndicate only for others we ara using values.votingQuorum,
        {
          accounts: {
            splGovernanceProgram,
            realm: realmAddress,
            realmConfig: realmConfigAddress,
            realmAuthority,
            payer: curator.publicKey,
            tokenOwnerRecord: tokenOwnerRecord,
            governance: governanceAddressTransfer,
            voterWeightRecord: voterWeightAddress,
            treasury: treasury,
            treasuryData,
            memberData: memberPDA,
            clubData,
            governedAccount: governedAccountTr,
            tokenProgram: TOKEN_PROGRAM_ID,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );
    createTreasuryGovernanceIx.push(createTransferGovernance);

    //CREATE FUNDRAISE
    const createFundraiseIx: TransactionInstruction[] = [];
    if (!values.skipFundraise) {
      const fundraiseInstruction = clubProgram.instruction.createFundraise(
        new BN(values.treasuryCap * Number(pow(10, values.mintDecimals))),
        {
          accounts: {
            memberData: memberPDA,
            treasuryData,
            treasury,
            fundraiseConfig: fundraiseAddress,
            payer: curator.publicKey,
            clubData,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );
      createFundraiseIx.push(fundraiseInstruction);
    }

    const chunkedMembersIx = chunk(addMemberInstructions, 2);

    let allInstructions: IInstructionSet[] = [];

    chunkedMembersIx.length > 0 &&
      chunkedMembersIx.forEach((ix) =>
        allInstructions.push({
          instructions: [...ix],
          description: "Adding initial list of members",
        })
      );

    createFundraiseIx.length > 0 &&
      allInstructions.unshift({
        instructions: [...createFundraiseIx],
        description: "Create fundraise",
      });

    if (sellPermissionsForSeparateTx.length > 0) {
      for (const sellPermission of sellPermissionsForSeparateTx) {
        const sellPermIx = sellPermissions.findIndex(
          (item) => item.from === sellPermission.from
        );
        const countBuff = Buffer.alloc(4);
        countBuff.writeInt32LE(sellPermIx, 0);

        const [sellPermissionAddress] = await PublicKey.findProgramAddress(
          [
            unqClubSeed,
            realmAddress.toBuffer(),
            sellPermissionGovernanceSeed,
            countBuff,
          ],
          clubProgram.programId
        );
        const [governanceAddress] = await PublicKey.findProgramAddress(
          [
            accountGovernanceSeed,
            realmAddress.toBuffer(),
            sellPermissionAddress.toBuffer(),
          ],
          splGovernanceProgram
        );

        const sellPermissionIx = clubProgram.instruction.addSellPermission(
          86400 * values.maxVoteTime + 1,
          sellPermission,
          {
            accounts: {
              splGovernanceProgram,
              realm: realmAddress,
              realmConfig: realmConfigAddress,
              realmAuthority,
              payer: curator.publicKey,
              tokenOwnerRecord: tokenOwnerRecord,
              governance: governanceAddress,
              voterWeightRecord: voterWeightAddress,
              treasury: treasury,
              treasuryData,
              memberData: memberPDA,
              clubData,
              governedSpc: sellPermissionAddress,
              tokenProgram: TOKEN_PROGRAM_ID,
              systemProgram: web3.SystemProgram.programId,
            },
          }
        );
        allInstructions.push({
          instructions: [updateVW, sellPermissionIx],
          description: `Add ${sellPermIx + 1}. sell permission`,
        });
      }
    }

    if (
      !retry ||
      (retry &&
        accountsNotCreated?.includes(ClubCreationAccounts.createTreasury))
    ) {
      createTreasuryGovernanceIx.length > 0 &&
        allInstructions.unshift({
          instructions: [...createTreasuryGovernanceIx],
          description: "Create Treasury governances",
        });

      if (values.mint.toString() !== NATIVE_MINT.toString()) {
        const {
          tokenAccount: denomTA,
          createAccountIx,
          createInitAccountInstruction,
        } = await createTokenAccountOwnedBySpecifiedAccount(
          curator.publicKey,
          profit,
          typeof values.mint === "string"
            ? new PublicKey(values.mint)
            : values.mint
        );

        const createDenomProfitTokenAccountIx: TransactionInstruction[] = [];
        createDenomProfitTokenAccountIx.push(createAccountIx);
        createDenomProfitTokenAccountIx.push(createInitAccountInstruction);
        allInstructions.unshift({
          instructions: [...createDenomProfitTokenAccountIx],
          description: "Create Financial account for denominated currency",
          partialSigner: denomTA,
        });
      }

      allInstructions.unshift({
        instructions: [...createProfitTokenAccountIx],
        description: "Create Financial account",

        partialSigner: tokenAccount,
      });
    }

    if (!retry) {
      allInstructions.unshift({
        instructions: [...instructions],
        description: `${
          createTreasuryGovernanceIx.length > 0
            ? "Create club and Vault account"
            : "Create club, Vault account, Treasury"
        }`,
      });
    }

    createOgRealmIx.length > 0 &&
      allInstructions.unshift({
        instructions: [...createOgRealmIx],
        description: "Create Realm",
      });

    if (allInstructions.length > 0) {
      await sendTransactions(
        RPC_CONNECTION,
        curator,
        allInstructions,
        SequenceType.Sequential,
        false,
        !retry,
        {
          values,
          clubData,
          realmAddress,
        },
        undefined,
        goToClubDetails
      );
      createNotification(MESSAGE_TYPE.SUCCESS, "Club created");
    }
    return {
      realmAddress,
      clubAddress: clubData,
    };
  } catch (error: any) {
    throw error;
  }
}

/**
 * Get club vault
 * @param clubName {string} Club name
 * @return {Promise<IDetailNftData[]>} Club details
 */
export async function getClubVault(clubName: string): Promise<IVault> {
  const clubProgram = programFactory();
  const [realmPda] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmPda.toBuffer()],
    clubProgram.programId
  );

  const [treasury] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  const [vaultAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasury.toBuffer(),
      vaultSeed,
      Buffer.from(CHAIN_ID),
    ],
    clubProgram.programId
  );

  return {
    nfts: await getNftsFromRPC(vaultAddress),
    vaultAddress: vaultAddress,
  };
}

/**
 * Creates treasury account for specific club
 * @param realmName Name of club
 * @returns Promise<IClubTreasury>
 */
export async function getTreasuryForClub(
  realmName: string
): Promise<IClubTreasury> {
  const clubProgram = programFactory();
  let denominatedTokenInfo: ITreasuryTokenAccInfo | undefined = undefined;

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [treasury] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const treasuryDataAccountInfo = await clubProgram.account.treasuryData.fetch(
    treasuryData
  );
  const fundraiseAccountsArray: IFundraise[] = [];
  const parsedAccountInfo: any = await RPC_CONNECTION.getParsedAccountInfo(
    treasuryDataAccountInfo.treasury
  );

  const sellPermissions = mapSellPermission(
    treasuryDataAccountInfo.sellPermission as ISellPermissionConfigSolana[]
  );

  const [profit] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), profitSeed],
    clubProgram.programId
  );
  const profitTokenAccounts =
    await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
      programId: TOKEN_PROGRAM_ID,
    });

  if (
    treasuryDataAccountInfo.denominatedCurrency !== null &&
    treasuryDataAccountInfo.financialTokenAccount !== null
  ) {
    denominatedTokenInfo = await getDenominatedTokenInfo(
      treasuryDataAccountInfo.financialTokenAccount,
      treasuryDataAccountInfo.denominatedCurrency,
      profitTokenAccounts.value.find(
        (item) =>
          item.account.data.parsed.info.mint ===
          treasuryDataAccountInfo.denominatedCurrency?.toString()
      )
    );
  }
  const rent = await RPC_CONNECTION.getMinimumBalanceForRentExemption(0);
  const treasuryAmount =
    (parsedAccountInfo.value.lamports - rent) / LAMPORTS_PER_SOL;

  const fundraiseAddresses: PublicKey[] = [];

  for (let i = 1; i <= treasuryDataAccountInfo.fundraiseCount; i++) {
    const countBuff = Buffer.alloc(4);
    countBuff.writeInt32LE(i, 0);

    const [fundraiseAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasuryData.toBuffer(), fundraiseCfgSeed, countBuff],
      clubProgram.programId
    );

    fundraiseAddresses.push(fundraiseAddress);
  }
  const fundraiseAccounts: any =
    await clubProgram.account.fundraiseConfig.fetchMultiple(fundraiseAddresses);

  for (const [index, fundraiseAccount] of fundraiseAccounts.entries()) {
    if (fundraiseAccount) {
      fundraiseAccountsArray.push({
        authority: fundraiseAccount.authority,
        capAmount:
          fundraiseAccount.capAmount.toNumber() /
          getDecimalsPerMint(denominatedTokenInfo),
        isActive: fundraiseAccount.isActive,
        raisedAmount:
          fundraiseAccount.raisedAmount.toNumber() /
          getDecimalsPerMint(denominatedTokenInfo),
        fundraiseConfigAddress: fundraiseAddresses[index],
        allocation: fundraiseAccount.allocation && {
          equal:
            fundraiseAccount.allocation.equal &&
            fundraiseAccount.allocation.equal.toNumber() /
              getDecimalsPerMint(denominatedTokenInfo),
          custom:
            fundraiseAccount.allocation.custom &&
            fundraiseAccount.allocation.custom.map((alloc) => {
              return {
                authority: alloc.authority,
                amount:
                  alloc.amount.toNumber() /
                  getDecimalsPerMint(denominatedTokenInfo),
              };
            }),
        },
      });
    }
  }

  const getTreasuryStatus = () => {
    if (treasuryDataAccountInfo.hasActiveFundraise) {
      return TreasuryFundraiseStatus.ActiveFundraise;
    }

    if (
      !treasuryDataAccountInfo.hasActiveFundraise &&
      fundraiseAccountsArray.length === 0
    ) {
      return TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises;
    }
    if (
      !treasuryDataAccountInfo.hasActiveFundraise &&
      fundraiseAccountsArray.length > 0
    ) {
      return TreasuryFundraiseStatus.NoActiveFundraiseWithPreviousFundraises;
    }
    return TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises;
  };

  const treasuryDataAccount = await clubProgram.account.treasuryData.fetch(
    treasuryData
  );

  const nativeProfitAmount = await getProfitForTreasury(treasury);

  const profitAmount = denominatedTokenInfo
    ? profitTokenAccounts.value.find(
        (item) =>
          item.account.data.parsed.info.mint ===
          treasuryDataAccountInfo.denominatedCurrency?.toString()
      )?.account.data.parsed.info.tokenAmount.uiAmount ?? 0
    : nativeProfitAmount;

  const treasuryTokenAccounts = await getTreasuyTokenAccountsInfo(
    treasury,
    treasuryAmount,
    nativeProfitAmount,
    profitTokenAccounts
  );
  const individualRights: any =
    treasuryDataAccountInfo.reservedRights?.individualRights;

  return {
    fundraiseAccounts: fundraiseAccountsArray,
    fundraiseCount: treasuryDataAccountInfo.fundraiseCount,
    governance: treasuryDataAccountInfo.governance,
    hasActiveFundraise: treasuryDataAccountInfo.hasActiveFundraise,
    treasury: treasuryDataAccountInfo.treasury,
    treasuryAmount: denominatedTokenInfo
      ? denominatedTokenInfo.balance
      : treasuryAmount + profitAmount,
    treasuryDataAddress: treasuryData,
    treasuryFundraiseStatus: getTreasuryStatus(),
    totalFundraised: getTotalFundraisedAmount(fundraiseAccountsArray),
    totalFinancialPower:
      treasuryDataAccountInfo.totalFinancialPower.toNumber() /
      getDecimalsPerMint(denominatedTokenInfo),
    sellPermissions,
    profitAmount,
    changeApprovalConfigGovernance:
      (treasuryDataAccount.changeClubConfigGovernance as PublicKey) ??
      undefined,
    withdrawalGovernance:
      (treasuryDataAccount.withdrawalGovernance as PublicKey) ?? undefined,
    transferGovernance:
      (treasuryDataAccount.transferGovernance as PublicKey) ?? undefined,
    tokenAccounts: treasuryTokenAccounts,
    denominatedTokenInfo,
    reservedRights: treasuryDataAccountInfo.reservedRights
      ? parseReserverRightsFromProgramToTreasuryInfo(
          treasuryDataAccountInfo.reservedRights,
          individualRights,
          getDecimalsPerMint(denominatedTokenInfo)
        )
      : undefined,
  };
}

/**
 *
 * Gets ownership and voting power for member
 *
 * @param member Member public key
 * @param clubName Name of club
 * @param clubType Club type(Deposit-based,Role-based,NFT-based)
 * @param userRole  User role(Owner,Curator,Member)
 * @param clubTreasury Treasury of club
 * @param roleBasedConfig Voting power config(for role-based clubs)
 * @returns IMembersRights
 */
export async function getRightsForClubMember(
  member: PublicKey,
  clubName: string,
  clubType: ClubType,
  userRole: string,
  clubTreasury: IClubTreasury,
  roleBasedConfig?: RoleConfig[],
  membersNfts?: number
): Promise<IMembersRights> {
  let ownershipForMember: number = 0;
  let votingPower: number = 0;
  const clubProgram = programFactory();

  const { depositRecords, depositedAmount } =
    await getDepositAmountForMemberBasedOnSelectedFundraises(
      clubTreasury.fundraiseCount,
      clubName,
      member,
      getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
    );
  try {
    const updatedTotalFinancialPower = divide(
      (
        await clubProgram.account.treasuryData.fetch(
          clubTreasury.treasuryDataAddress
        )
      ).totalFinancialPower.toNumber(),
      getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
    );

    const roundDecimals =
      clubTreasury.denominatedTokenInfo?.decimals ?? NATIVE_MINT_DECIMAL_PLACES;
    let financialPower = 0;

    if (depositedAmount === 0 || updatedTotalFinancialPower === 0) {
      ownershipForMember = 0;
    } else {
      financialPower = depositedAmount;
      ownershipForMember = round(
        divide(
          depositedAmount *
            getDecimalsPerMint(clubTreasury.denominatedTokenInfo),
          updatedTotalFinancialPower *
            getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
        ),
        roundDecimals
      );
    }

    if (clubTreasury.reservedRights) {
      const memberReservedRights =
        clubTreasury.reservedRights.individualRights.find(
          (item) => item.memberPubkey.toString() === member.toString()
        );
      if (
        memberReservedRights &&
        memberReservedRights.amountOfRights > 0 &&
        updatedTotalFinancialPower > 0
      ) {
        financialPower += memberReservedRights.amountOfRights;
        ownershipForMember = round(
          add(
            ownershipForMember,
            divide(
              memberReservedRights.amountOfRights *
                getDecimalsPerMint(clubTreasury.denominatedTokenInfo),
              updatedTotalFinancialPower *
                getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
            )
          ),
          roundDecimals
        );
      }
    }
    if (clubType === ClubType.RoleBased && roleBasedConfig) {
      const userRoleConfig = roleBasedConfig.find(
        (item) => item.name === userRole
      );
      if (!userRoleConfig) {
        throw new Error("Problem with loading user voting power");
      }
      votingPower = userRoleConfig.roleWeight / LAMPORTS_PER_SOL;
    } else if (
      clubType === ClubType.TokenProportional ||
      clubType === ClubType.ProjectFundraise
    ) {
      votingPower = ownershipForMember;
    } else {
      votingPower = membersNfts!;
    }

    return {
      ownership: round(ownershipForMember * 100, 2),
      financialPower: round(
        financialPower,
        clubTreasury?.denominatedTokenInfo?.decimals ??
          NATIVE_MINT_DECIMAL_PLACES
      ),
      votingPower,
      depositedAmount:
        depositRecords.length > 0
          ? +depositRecords[
              depositRecords.length - 1
            ].accumulatedAmount.toFixed(9) /
            getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
          : 0,
      depositRecords,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Get NFTs with metadata from users wallet specified by Club's collection
 *
 * @param wallet Member wallet {AnchorWallet}
 * @param communityMint Community token mint of specific realm {PublicKey}
 * @returns IDetailNftData[]
 */
export const getUsersNfts = async (
  wallet: PublicKey,
  communityMint: PublicKey
) => {
  const metadataWallet = (
    await Metadata.findDataByOwner(RPC_CONNECTION, wallet)
  ).filter(
    (item) =>
      item.collection?.verified &&
      item.collection.key === communityMint.toBase58()
  );
  const nfts: IDetailNftData[] = [];

  const tokenAccounts = await getNftTokenAccountsByOwner(wallet);

  for (const metaData of metadataWallet) {
    const metaDataFromUri = await fetch(metaData.data.uri);
    const metaDataJson = await metaDataFromUri.json();
    const metadataAddress = await Metadata.getPDA(metaData.mint);
    const tokenAcc = tokenAccounts.find(
      (account) => account.account.data.parsed.info.mint === metaData.mint
    );
    nfts.push({
      collection: {
        family: metaDataJson.collection?.family,
        name: metaDataJson.collection?.name,
      },
      mint: new PublicKey(metaData.mint),
      name: metaData.data.name,
      nftUrl: metaDataJson.image,
      description: metaDataJson.description,
      metadataAddress,
      tokenAccount: tokenAcc?.pubkey,
    });
  }
  return nfts;
};

/**
 * Get user's role in a club
 * @param member {AnchorWallet} Member
 * @param clubName {string} Name of club
 * @returns {Promise<IClubMemberBasicInfo | undefined>}
 */
export async function getUserRoleForClub(
  member: AnchorWallet,
  clubName: string
): Promise<IClubMemberBasicInfo | undefined> {
  const clubProgram = programFactory();
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      member.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );
  try {
    const member = await clubProgram.account.allowedMemberData.fetch(memberPDA);

    if (
      member.clubData.toString() === clubData.toString() &&
      member.status === InvitationStatus.Accepted
    ) {
      return {
        pubkey: member.memberPubkey,
        role: member.role,
      };
    } else {
      return undefined;
    }
  } catch (error: any) {
    console.log(error);
    throw error.message;
  }
}

/**
 * Creates fundraise for specific club
 *
 * @param clubName Name of club
 * @param capAmount Amount to raise during fundraise
 * @param treasury Treasury of club
 * @param fundraiseCount Number of prior fundraises in club
 * @param payer Payer{AnchorWallet}
 * @return {Promise<void>}
 */
export async function createFundraise(
  clubName: string,
  capAmount: number,
  treasury: IClubTreasury,
  fundraiseCount: number,
  payer: AnchorWallet,
  allocation: ISolanaAllocation | null,
  reservedRights?: ISolanaReservedRights
): Promise<void> {
  const clubProgram = programFactory();
  const allInstructions: IInstructionSet[] = [];

  const fundraiseCountBuff = Buffer.alloc(4);
  fundraiseCountBuff.writeInt32LE(fundraiseCount, 0);

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [fundraiseAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      fundraiseCfgSeed,
      fundraiseCountBuff,
    ],
    clubProgram.programId
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const createFundraiseAndReserveRightsIfNeeded: TransactionInstruction[] = [];

  if (reservedRights) {
    const reservedRightsIx = clubProgram.instruction.reserveRights(
      reservedRights,
      {
        accounts: {
          memberData: memberPDA,
          treasuryData: treasuryData,
          payer: payer.publicKey,
          clubData,
          systemProgram: web3.SystemProgram.programId,
        },
      }
    );
    createFundraiseAndReserveRightsIfNeeded.push(reservedRightsIx);
  }

  const fundraiseInstruction = clubProgram.instruction.createFundraise(
    new BN(capAmount * getDecimalsPerMint(treasury.denominatedTokenInfo)),
    {
      accounts: {
        memberData: memberPDA,
        treasuryData,
        treasury: treasury.treasury,
        fundraiseConfig: fundraiseAddress,
        payer: payer.publicKey,
        clubData,
        systemProgram: web3.SystemProgram.programId,
      },
    }
  );
  createFundraiseAndReserveRightsIfNeeded.push(fundraiseInstruction);
  allInstructions.push({
    description: "Create fundraise",
    instructions: createFundraiseAndReserveRightsIfNeeded,
  });

  if (allocation) {
    const allocationInstructionSet = await updateAllocationForFundraise(
      clubName,
      treasury,
      fundraiseCount,
      payer,
      allocation.equal,
      allocation.custom,
      false,
      null
    );
    allocationInstructionSet.forEach((item) => allInstructions.push(item));
  }

  await sendTransactions(RPC_CONNECTION, payer, allInstructions);
}

/**
 *
 * Closing active fundraise
 *
 * @param clubName Name of club
 * @param wallet Wallet of club owner{AnchorWallet}
 * @param treasury Club's treasury
 * @return {Promise<void>}
 */
export async function finishFundraise(
  clubName: string,
  wallet: AnchorWallet,
  treasury: IClubTreasury
): Promise<void> {
  const clubProgram = programFactory();

  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const fundraiseCountBuff = Buffer.alloc(4);
  fundraiseCountBuff.writeInt32LE(treasury.fundraiseCount, 0);

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [fundraiseConfigAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      fundraiseCfgSeed,
      fundraiseCountBuff,
    ],
    clubProgram.programId
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      wallet.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash()).blockhash;

  const tx = new Transaction({ feePayer: wallet.publicKey, recentBlockhash });

  const finishFundraiseIx = clubProgram.instruction.finishFundraise({
    accounts: {
      memberData: memberPDA,
      treasuryData,
      treasury: treasury.treasury,
      fundraiseConfig: fundraiseConfigAddress,
      clubData,
      payer: wallet.publicKey,
      systemProgram: web3.SystemProgram.programId,
    },
  });

  tx.add(finishFundraiseIx);

  const signedTx = await wallet.signTransaction(tx);

  await sendTransaction({
    connection: RPC_CONNECTION,
    transaction: signedTx,
    wallet,
  });
}

/**
 * Get remaining accounts for selected sell permissions
 * @param sellPermissions {ISellPermissionConfigSolana[]} Array of sell permissions
 * @param realmAddress {PublicKey} Realm address
 * @param clubProgram {Program<ClubProgram>} Club program
 * @returns {Promise<AccountMeta[]>}
 */
const getSellPermissionsRemainingAccounts = async (
  sellPermissions: ISellPermissionConfigSolana[],
  realmAddress: PublicKey,
  clubProgram: Program<ClubProgram>
): Promise<AccountMeta[]> => {
  const remainingAccounts: AccountMeta[] = [];
  for (const [index] of sellPermissions.entries()) {
    const countBuff = Buffer.alloc(4);
    countBuff.writeInt32LE(index, 0);

    const [sellPermissionAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        realmAddress.toBuffer(),
        sellPermissionGovernanceSeed,
        countBuff,
      ],
      clubProgram.programId
    );
    const [governanceAddress] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmAddress.toBuffer(),
        sellPermissionAddress.toBuffer(),
      ],
      splGovernanceProgram
    );

    remainingAccounts.push(
      {
        pubkey: governanceAddress,
        isWritable: true,
        isSigner: false,
      },
      {
        pubkey: sellPermissionAddress,
        isWritable: false,
        isSigner: false,
      }
    );
  }
  return remainingAccounts;
};

/**
 * Get all governances for selected club
 * @param realmName {string} Club name
 * @returns {Promise<ProgramAccount<Governance>[]>}
 */
export const getGovernaceAccountsForRealm = async (
  realmName: string
): Promise<ProgramAccount<Governance>[]> => {
  try {
    const clubProgram = programFactory();
    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [treasury] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasurySeed,
        getTreasuryCount(TREASURY_INDEX),
      ],
      clubProgram.programId
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    const treasuryDataAccount = await clubProgram.account.treasuryData.fetch(
      treasuryData
    );

    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );
    const [withdrawalGovernanceAddress] = await PublicKey.findProgramAddress(
      [accountGovernanceSeed, realmAddress.toBuffer(), vaultAddress.toBuffer()],
      splGovernanceProgram
    );

    const [defaultGovernanceAddress] = await PublicKey.findProgramAddress(
      [accountGovernanceSeed, realmAddress.toBuffer(), treasury.toBuffer()],
      splGovernanceProgram
    );
    const governanceAddresses: PublicKey[] = [];
    governanceAddresses.push(
      defaultGovernanceAddress,
      withdrawalGovernanceAddress
    );

    if (treasuryDataAccount.transferGovernance !== null) {
      governanceAddresses.push(treasuryDataAccount.transferGovernance);
    }
    if (treasuryDataAccount.changeClubConfigGovernance !== null) {
      governanceAddresses.push(treasuryDataAccount.changeClubConfigGovernance);
    }
    for (let i = 0; i < MAX_SELL_PERMISSIONS_FOR_CLUB; i++) {
      const countBuffer = Buffer.alloc(4);
      countBuffer.writeInt32LE(i, 0);

      const [sellPermissionAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          realmAddress.toBuffer(),
          sellPermissionGovernanceSeed,
          countBuffer,
        ],
        clubProgram.programId
      );
      const [sellPermissionGovernance] = await PublicKey.findProgramAddress(
        [
          accountGovernanceSeed,
          realmAddress.toBuffer(),
          sellPermissionAddress.toBuffer(),
        ],
        splGovernanceProgram
      );
      governanceAddresses.push(sellPermissionGovernance);
    }
    const chunkedGovernances = chunk(governanceAddresses, 100);
    const governanceAccounts: ProgramAccount<Governance>[] = [];
    for (const chunkedGovernancePubkeys of chunkedGovernances) {
      const rawGovernanceAccounts =
        await RPC_CONNECTION.getMultipleAccountsInfo(chunkedGovernancePubkeys);
      for (const [index, rawGovernance] of rawGovernanceAccounts.entries()) {
        if (!rawGovernance) continue;
        const governanceAccount = deserializeBorsh(
          GOVERNANCE_SCHEMA,
          Governance,
          rawGovernance.data
        );
        governanceAccounts.push({
          account: governanceAccount,
          owner: splGovernanceProgram,
          pubkey: governanceAddresses[index],
        });
      }
    }
    return governanceAccounts;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 *
 * @param clubAddress Address of club whose roles are updated
 */
export async function refreshRoleConfig(
  clubAddress: PublicKey
): Promise<RoleConfig[]> {
  try {
    const clubProgram = programFactory();
    const clubData = await clubProgram.account.clubData.fetch(clubAddress);
    const roleConfigRaw: any = clubData.roleConfig;
    return roleConfigRaw.map((item) => {
      return {
        name: item.name,
        roleWeight: item.roleWeight.toNumber(),
        actions: item.clubActions.map((item) =>
          mapClubActionFromSolanaToClubAction(
            Object.keys(item).toString() as ClubActionSolana
          )
        ),
      };
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}
export const mapDepositRecords = (depositRecord: any): IDepositRecord => {
  return {
    accumulatedAmount: depositRecord.accumulatedAmount.toNumber(),
    currentFundraiseDeposit: depositRecord.currentFundraiseDeposit.toNumber(),
    depositedAt: depositRecord.depositedAt.toNumber(),
    fundraiseIndex: depositRecord.fundraiseIndex,
    tradedRight:
      depositRecord.tradedRight?.map((tr) => {
        if (tr) {
          return {
            amount: tr.amount.toNumber(),
            timestamp: tr.timestamp.toNumber(),
            tradedType: Object.keys(tr.tradeType)[0] as TradedRightType,
          };
        }
      }) ?? [],
  };
};

export const updateAllocationForFundraise = async (
  clubName: string,
  treasury: IClubTreasury,
  fundraiseCount: number,
  payer: AnchorWallet,
  equal: BN | null,
  custom: ISolanaCustomAllocation[] | null,
  isRemoving: boolean,
  newCap: BN | null,
  equalChanged?: boolean
): Promise<IInstructionSet[]> => {
  try {
    const clubProgram = programFactory();
    const allInstructions: IInstructionSet[] = [];

    const fundraiseCountBuff = Buffer.alloc(4);
    fundraiseCountBuff.writeInt32LE(fundraiseCount, 0);

    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(clubName)],
      splGovernanceProgram
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    const [fundraiseAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        treasuryData.toBuffer(),
        fundraiseCfgSeed,
        fundraiseCountBuff,
      ],
      clubProgram.programId
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        payer.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    if (custom !== null && custom.length > 0) {
      const chunkedCustomAllocation: ISolanaCustomAllocation[][] = chunk(
        custom,
        10
      );
      chunkedCustomAllocation.forEach((element) => {
        const updateAllocationIx = clubProgram.instruction.updateAllocation(
          equal,
          !isRemoving ? element : null,
          isRemoving ? element : null,
          newCap,
          {
            accounts: {
              clubData,
              memberData: memberPDA,
              treasuryData,
              fundraiseConfig: fundraiseAddress,
              payer: payer.publicKey,
              systemProgram: SystemProgram.programId,
            },
          }
        );
        allInstructions.push({
          description: "Update allocation for members",
          instructions: [updateAllocationIx],
        });
      });
    } else {
      const updateAllocationIx = clubProgram.instruction.updateAllocation(
        equal,
        null,
        null,
        newCap,
        {
          accounts: {
            clubData,
            memberData: memberPDA,
            treasuryData,
            fundraiseConfig: fundraiseAddress,
            payer: payer.publicKey,
            systemProgram: SystemProgram.programId,
          },
        }
      );
      allInstructions.push({
        description:
          !newCap || equalChanged
            ? "Update allocation for members"
            : "Update fundraise cap",
        instructions: [updateAllocationIx],
      });
    }
    return allInstructions;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const updateFundraiseConfiguration = async (
  clubName: string,
  treasury: IClubTreasury,
  fundraiseCount: number,
  payer: AnchorWallet,
  newCap: number,
  equal: BN | null,
  customAllocations?: ISolanaCustomAllocation[],
  removedCustomAllocations?: ISolanaCustomAllocation[],
  equalChanged?: boolean
) => {
  try {
    const allInstructions: IInstructionSet[] = [];
    const decimals = await getDecimalsPerMint(treasury.denominatedTokenInfo);
    const newCapAmount: BN | null =
      treasury.fundraiseAccounts.find((acc) => acc.isActive)?.capAmount !==
      newCap
        ? new BN(decimals * newCap)
        : null;
    let updatedCapAmount = false;
    if (
      (removedCustomAllocations && removedCustomAllocations.length > 0) ||
      newCapAmount
    ) {
      updatedCapAmount = !!newCapAmount;
      const updatedConfig = await updateAllocationForFundraise(
        clubName,
        treasury,
        fundraiseCount,
        payer,
        equal,
        removedCustomAllocations ?? null,
        true,
        newCapAmount
      );

      updatedConfig.forEach((item) => {
        allInstructions.push(item);
      });
    }
    if (
      (customAllocations && customAllocations.length > 0) ||
      (newCapAmount && !updatedCapAmount) ||
      equalChanged
    ) {
      const updatedConfig = await updateAllocationForFundraise(
        clubName,
        treasury,
        fundraiseCount,
        payer,
        equal,
        customAllocations ?? null,
        false,
        newCapAmount,
        equalChanged
      );

      updatedConfig.forEach((item) => {
        allInstructions.push(item);
      });
    }

    await sendTransactions(RPC_CONNECTION, payer, allInstructions);
  } catch (error) {
    throw error;
  }
};

/**
 *
 * Closing active fundraise
 *
 * @param clubName Name of club
 * @param wallet Wallet of club owner{AnchorWallet}
 * @param treasury Club's treasury
 * @param depositAmount amount
 * @param member Member pubkey
 * @return {Promise<void>}
 */
export async function migrateFinancials(
  clubName: string,
  invitedMembers: IExistingInvestment[],
  wallet: AnchorWallet,
  treasury: IClubTreasury
): Promise<void> {
  const clubProgram = programFactory();

  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const realm = await getRealm(RPC_CONNECTION, realmAddress);
  const governingTokenMint = realm.account.communityMint;

  const fundraiseCountBuff = Buffer.alloc(4);
  fundraiseCountBuff.writeInt32LE(treasury.fundraiseCount + 1, 0);

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [fundraiseConfigAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      fundraiseCfgSeed,
      fundraiseCountBuff,
    ],
    clubProgram.programId
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [payerMemberData] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      wallet.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const addMemberInstructions: TransactionInstruction[] = [];

  for (const invitedMember of invitedMembers) {
    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        new PublicKey(invitedMember.wallet).toBuffer(),
      ],
      clubProgram.programId
    );

    const [financialRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        treasuryData.toBuffer(),
        financialRecordSeed,
        new PublicKey(invitedMember.wallet).toBuffer(),
      ],
      clubProgram.programId
    );

    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmAddress.toBuffer(),
        governingTokenMint.toBuffer(),
        new PublicKey(invitedMember.wallet).toBuffer(),
      ],
      splGovernanceProgram
    );

    const numberOfDecimals = getDecimalsPerMint(treasury.denominatedTokenInfo);

    const membersInstruction = clubProgram.instruction.migrateFinancials(
      new BN(
        Number(extractNumbersFromString(invitedMember.amount)) *
          numberOfDecimals
      ),
      {
        accounts: {
          governingTokenMint,
          realm: realmAddress,
          payerMemberData,
          splGovernance: splGovernanceProgram,
          tokenOwnerRecord,
          payer: wallet.publicKey,
          treasuryData,
          clubData,
          member: new PublicKey(invitedMember.wallet),
          memberData: memberPDA,
          financialRecord,
          fundraiseConfig: fundraiseConfigAddress,
          systemProgram: web3.SystemProgram.programId,
        },
      }
    );
    addMemberInstructions.push(membersInstruction);
  }
  const chunkedMembersIx = chunk(addMemberInstructions, 2);

  const allInstructions: IInstructionSet[] = [];

  chunkedMembersIx.length > 0 &&
    chunkedMembersIx.forEach((ix) =>
      allInstructions.push({
        instructions: [...ix],
        description: "Adding initial list of members",
      })
    );

  await sendTransactions(
    RPC_CONNECTION,
    wallet,
    allInstructions,
    SequenceType.Sequential,
    false,
    false,
    undefined
  );
}
